import {
    getPlacementQuizApiV1PlacementQuizGet,
    evaluateLevelPlacementApiV1PlacementQuizEvaluateLevelPost,
    submitPlacementQuizApiV1PlacementQuizSubmitPost,
    getCoursePlansApiV1CoursesCourseIdPlansGet,
    updateUserSettingsApiV1UsersSettingsPatch,
} from "@/src/api/sdk.gen";

// import PlacementQuizClient, {Loader2, Trophy } from "./QuizClient";
import { Link } from "@/src/lib/next-compat";
import { use, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useCourseNavigation } from "@/lib/contexts/CourseNavigationContext";
import { invalidateCourseCore } from "@/lib/student/course-core-query";
import { useState } from "react";
import { AlertCircle, ArrowRight, BookOpen, Brain, CheckCircle, ChevronLeft, ChevronRight, Clock, Infinity, Languages, Loader2, Repeat, Trophy, XCircle, Zap } from "lucide-react";
import { normalizeGeneratedText } from "@/lib/normalize-generated-text";
import { useUserProfile } from "@/src/lib/hooks/useUserProfile";

interface QuizQuestion {
    id: number; // Question IDs remain integers internally
    question_text: string;
    options: (string | { label?: string; text?: string; value?: string })[];
    level_name: string;
}

interface QuizData {
    id: string | number; // Quiz ID can be UUID or integer
    title: string;
    description: string;
    questions: QuizQuestion[];
    version: number;
    language?: string;
    availableLanguages?: string[];
    passedLevels?: string[];
    failedLevels?: string[];
    attemptsUsed?: number;
    maxAttempts?: number;
    attemptsRemaining?: number;
}

function normalizePlacementQuestion(question: QuizQuestion): QuizQuestion {
    return {
        ...question,
        question_text: normalizeGeneratedText(question.question_text),
        level_name: normalizeGeneratedText(question.level_name),
        options: (question.options || []).map((option) => {
            if (typeof option === "string") return normalizeGeneratedText(option);
            return {
                ...option,
                label: option.label ? normalizeGeneratedText(option.label) : option.label,
                text: option.text ? normalizeGeneratedText(option.text) : option.text,
                value: option.value ? normalizeGeneratedText(option.value) : option.value,
            };
        }),
    };
}

export default function CoursePlacementQuizPage({ params }: { params?: Promise<{ courseId: string }> } = {}) {
    const routeParams = useParams<{ courseId: string }>();
    const courseId = routeParams.courseId || "";
    const navigate = useNavigate();
    const nav = useCourseNavigation();
    const queryClient = useQueryClient();
    const { data: userProfile } = useUserProfile();
    const isOrgMember = (userProfile?.organizations?.length ?? 0) > 0;

    const [quiz, setQuiz] = useState<QuizData | null>(null);
    const [levels, setLevels] = useState<{ name: string; questions: QuizQuestion[] }[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [step, setStep] = useState<"intro" | "quiz" | "level_interstitial" | "result">("intro");

    const [passedLevels, setPassedLevels] = useState<string[]>([]);
    const [failedLevels, setFailedLevels] = useState<string[]>([]);
    const [attemptsUsed, setAttemptsUsed] = useState(0);
    const [maxAttempts, setMaxAttempts] = useState(1);
    const [attemptsRemaining, setAttemptsRemaining] = useState(1);
    const [minAllowedLevelIdx, setMinAllowedLevelIdx] = useState(0);
    const [isMaxAttemptsReached, setIsMaxAttemptsReached] = useState(false);

    const [currentLevelIdx, setCurrentLevelIdx] = useState(0);
    const [questionIdxInLevel, setQuestionIdxInLevel] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({}); // Mapping question.id -> answer

    const [placementLevel, setPlacementLevel] = useState<string | null>(null);
    const [suggestedPlanId, setSuggestedPlanId] = useState<number | null>(null);
    const [yearlyPlanId, setYearlyPlanId] = useState<number | null>(null);
    const [selectedPlanType, setSelectedPlanType] = useState<"lifetime" | "yearly">("lifetime");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isEvaluating, setIsEvaluating] = useState(false);
    const [levelResult, setLevelResult] = useState<{ passed: boolean; percentage: number } | null>(null);
    const [isAlreadyTaken, setIsAlreadyTaken] = useState(false);
    const [accessMode, setAccessMode] = useState<"SPONSORED" | "INDIVIDUAL" | null>(null);
    const [organizationName, setOrganizationName] = useState<string | null>(null);

    const [currentLanguage, setCurrentLanguage] = useState<"English" | "Arabic">(() => {
        if (typeof window !== "undefined") {
            const p = new URLSearchParams(window.location.search).get("lang") || new URLSearchParams(window.location.search).get("language");
            if (p?.toLowerCase() === "ar" || p?.toLowerCase() === "arabic") return "Arabic";
        }
        return "English";
    });
    const [switchingLanguage, setSwitchingLanguage] = useState(false);

    const handleSwitchLanguage = async (newLang: "English" | "Arabic") => {
        if (newLang === currentLanguage || switchingLanguage) return;
        setSwitchingLanguage(true);
        setCurrentLanguage(newLang);
        try {
            const isNumericCourseId = /^\d+$/.test(courseId);
            const { data: resData } = await getPlacementQuizApiV1PlacementQuizGet({
                query: {
                    course_id: isNumericCourseId ? Number.parseInt(courseId, 10) : (courseId as any),
                    language: newLang,
                } as any,
            });
            if (resData) {
                const data = resData as unknown as QuizData & { courseUuid?: string };
                const normalizedQuestions = Array.isArray(data.questions)
                    ? data.questions.map((q: QuizQuestion) => normalizePlacementQuestion(q))
                    : [];
                setQuiz({
                    ...data,
                    title: normalizeGeneratedText(data.title ?? ""),
                    description: normalizeGeneratedText(data.description ?? ""),
                    questions: normalizedQuestions,
                });
            }
            if (typeof window !== "undefined") {
                const url = new URL(window.location.href);
                url.searchParams.set("lang", newLang === "Arabic" ? "ar" : "en");
                window.history.replaceState({}, "", url.toString());
            }
            try {
                await updateUserSettingsApiV1UsersSettingsPatch({
                    body: {
                        course_id: /^\d+$/.test(courseId) ? parseInt(courseId, 10) : undefined,
                        language: newLang,
                    },
                });
            } catch {}
        } catch (err) {
            console.error("Failed to switch quiz language:", err);
        } finally {
            setSwitchingLanguage(false);
        }
    };

    useEffect(() => {
        const fetchQuiz = async () => {
            try {
                const isNumericCourseId = /^\d+$/.test(courseId);
                const searchParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
                const langParam = searchParams?.get("lang") || searchParams?.get("language") || currentLanguage;
                const { data: resData, error: reqErr, response } = await getPlacementQuizApiV1PlacementQuizGet({
                    query: {
                        course_id: isNumericCourseId ? Number.parseInt(courseId, 10) : (courseId as any),
                        ...(langParam ? { language: langParam } : {}),
                    } as any,
                });

                if (reqErr || !resData) {
                    if (response?.status === 403) {
                        const errBody = (reqErr || {}) as {
                            purchaseRequired?: boolean;
                            alreadyDetermined?: boolean;
                        };
                        if (errBody.purchaseRequired) {
                            navigate(`/courses/${courseId}/plans`);
                            return;
                        }
                        if (errBody.alreadyDetermined) {
                            setIsAlreadyTaken(true);
                            setLoading(false);
                            return;
                        }
                        setLoading(false);
                        return;
                    }
                    if (response?.status === 404) {
                        throw new Error("No approved placement quiz found for this course.");
                    }
                    throw new Error("Failed to fetch placement quiz.");
                }
                const data = resData as unknown as QuizData & { courseUuid?: string };
                const normalizedQuestions = Array.isArray(data.questions)
                    ? data.questions.map((q: QuizQuestion) => normalizePlacementQuestion(q))
                    : [];
                const normalizedQuiz: QuizData = {
                    ...data,
                    title: normalizeGeneratedText(data.title ?? ""),
                    description: normalizeGeneratedText(data.description ?? ""),
                    questions: normalizedQuestions,
                };
                setQuiz(normalizedQuiz);

                const apiPassedLevels = Array.isArray(data.passedLevels) ? data.passedLevels : [];
                const apiFailedLevels = Array.isArray((data as any).failedLevels) ? (data as any).failedLevels : [];
                setPassedLevels(apiPassedLevels);
                setFailedLevels(apiFailedLevels);
                const used = data.attemptsUsed ?? 0;
                const max = 1;
                const isDetermined = Boolean((data as any).placementDetermined);
                const remaining = isDetermined ? 0 : (data.attemptsRemaining ?? Math.max(0, max - used));
                setAttemptsUsed(used);
                setMaxAttempts(max);
                setAttemptsRemaining(remaining);
                if (remaining <= 0 || isDetermined || used >= 1) {
                    setIsMaxAttemptsReached(true);
                }
                if ((data as any).currentPlacementLevel) {
                    setPlacementLevel((data as any).currentPlacementLevel);
                }

                // Redirect to UUID URL if current courseId is numeric/doesn't match the actual course UUID
                if (data.courseUuid && courseId !== data.courseUuid) {
                    navigate(`/placement-quiz/${data.courseUuid}`);
                }

                // Group questions by level
                const grouped: Record<string, QuizQuestion[]> = {};
                normalizedQuiz.questions.forEach((q: QuizQuestion) => {
                    if (!grouped[q.level_name]) grouped[q.level_name] = [];
                    grouped[q.level_name].push(q);
                });

                // Maintain the order provided by the API
                const orderedNames: string[] = [];
                normalizedQuiz.questions.forEach((q: QuizQuestion) => {
                    if (!orderedNames.includes(q.level_name)) {
                        orderedNames.push(q.level_name);
                    }
                });

                const passedSet = new Set(apiPassedLevels.map((l: string) => l.toLowerCase()));
                const allPassed = orderedNames.length > 0 && orderedNames.every((name) => passedSet.has(name.toLowerCase()));
                let startIdx = orderedNames.findIndex((name) => !passedSet.has(name.toLowerCase()));
                if (allPassed || startIdx === -1) {
                    // All levels in this placement quiz have already been passed
                    setIsMaxAttemptsReached(true);
                    setPlacementLevel((data as any).currentPlacementLevel || orderedNames[orderedNames.length - 1] || "Intermediate");
                    startIdx = 0;
                }
                setCurrentLevelIdx(startIdx);
                setMinAllowedLevelIdx(startIdx);

                setLevels(orderedNames.map(name => ({ name, questions: grouped[name] })));
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : "An unexpected error occurred");
            } finally {
                setLoading(false);
            }
        };

        fetchQuiz();
    }, [courseId]);

    useEffect(() => {
        if (step !== "result") return;
        let cancelled = false;
        const loadAccessMode = async () => {
            try {
                const { data } = await getCoursePlansApiV1CoursesCourseIdPlansGet({
                    path: { course_id: courseId as any },
                });
                const json = (data || {}) as { accessMode?: "SPONSORED" | "INDIVIDUAL"; organizationName?: string | null };
                if (cancelled) return;
                setAccessMode(json.accessMode ?? "INDIVIDUAL");
                setOrganizationName(json.organizationName ?? null);
            } catch {
                if (!cancelled) setAccessMode("INDIVIDUAL");
            }
        };
        void loadAccessMode();
        return () => {
            cancelled = true;
        };
    }, [courseId, step]);

    const handleStart = () => {
        setCurrentLevelIdx(minAllowedLevelIdx);
        setQuestionIdxInLevel(0);
        setStep("quiz");
    };

    const handleAnswerSelect = (questionId: number, option: string) => {
        setSelectedAnswers(prev => ({
            ...prev,
            [questionId]: option
        }));
        if (submitError) setSubmitError(null);
    };

    const currentLevel = levels[currentLevelIdx];
    const currentQuestion = currentLevel?.questions[questionIdxInLevel];
    const totalQuestions = levels.reduce((acc, l) => acc + l.questions.length, 0);
    const averageQuestionsPerLevel =
        levels.length > 0 ? Math.round(totalQuestions / levels.length) : totalQuestions;
    const estimatedMinutesRaw = Math.max(10, totalQuestions * 1.5);
    const estimatedMinutes = Math.round(estimatedMinutesRaw / 10) * 10;
    const globalIdx = levels.slice(0, currentLevelIdx).reduce((acc, l) => acc + l.questions.length, 0) + questionIdxInLevel;
    const canGoPrevious = questionIdxInLevel > 0 || currentLevelIdx > minAllowedLevelIdx;
    const isLastLevel = currentLevelIdx === levels.length - 1;

    const handlePrevious = () => {
        if (questionIdxInLevel > 0) {
            setQuestionIdxInLevel(prev => prev - 1);
        } else if (currentLevelIdx > minAllowedLevelIdx) {
            const prevLevelIdx = currentLevelIdx - 1;
            setCurrentLevelIdx(prevLevelIdx);
            setQuestionIdxInLevel(levels[prevLevelIdx].questions.length - 1);
        }
    };

    const handleNext = async () => {
        if (!currentLevel) return;

        if (questionIdxInLevel < currentLevel.questions.length - 1) {
            setQuestionIdxInLevel(prev => prev + 1);
        } else {
            // Finished a level! Evaluate it.
            await evaluateCurrentLevel();
        }
    };

    const evaluateCurrentLevel = async () => {
        setIsEvaluating(true);
        setSubmitError(null);
        try {
            const levelAnswers = currentLevel.questions.map(q => ({
                questionId: q.id,
                answer: selectedAnswers[q.id] || ""
            }));

            const { data, error } = await evaluateLevelPlacementApiV1PlacementQuizEvaluateLevelPost({
                body: {
                    levelName: currentLevel.name,
                    answers: levelAnswers,
                } as any,
            });

            if (error || !data) throw new Error("Failed to evaluate level.");

            const evalRes = data as any;
            setLevelResult(evalRes);

            // If terminal (failed or last level), submit automatically
            if (!evalRes.passed || isLastLevel) {
                await submitQuiz();
            } else {
                setStep("level_interstitial");
            }
        } catch (err: unknown) {
            setSubmitError(err instanceof Error ? err.message : "An unexpected error occurred");
        } finally {
            setIsEvaluating(false);
        }
    };

    const handleProceed = () => {
        if (levelResult?.passed && currentLevelIdx < levels.length - 1) {
            // On to next level
            setCurrentLevelIdx(prev => prev + 1);
            setQuestionIdxInLevel(0);
            setLevelResult(null);
            setStep("quiz");
        }
    };

    const [firstLessonUuid, setFirstLessonUuid] = useState<string | null>(null);

    const submitQuiz = async () => {
        if (!quiz) return;

        setIsSubmitting(true);
        setSubmitError(null);
        try {
            // Submit all answers collected so far
            const formattedAnswers = Object.entries(selectedAnswers).map(([qId, ans]) => ({
                questionId: parseInt(qId),
                answer: ans
            }));

            const { data: resData, error: reqErr } = await submitPlacementQuizApiV1PlacementQuizSubmitPost({
                body: {
                    quizId: Number(quiz.id),
                    answers: formattedAnswers,
                } as any,
            });

            if (reqErr || !resData) {
                const errObj = (reqErr || {}) as { error?: string; detail?: string };
                throw new Error(errObj.detail || errObj.error || "Failed to submit quiz results.");
            }

            const data = resData as any;

            // Bust the prefetched course cache so every return path back to
            // the course detail page picks up the new placement state.
            nav?.setPrefetchedCourseData(courseId, null);
            setPlacementLevel(data.placementLevel);
            setPassedLevels(Array.isArray(data.passedLevels) ? data.passedLevels : []);
            setFailedLevels(Array.isArray(data.failedLevels) ? data.failedLevels : []);
            setFirstLessonUuid(data.firstLessonUuid);
            setSuggestedPlanId(typeof data.suggestedPlanId === "number" ? data.suggestedPlanId : null);
            setYearlyPlanId(typeof data.yearlyPlanId === "number" ? data.yearlyPlanId : null);
            setAttemptsUsed(prev => prev + 1);
            setAttemptsRemaining(prev => Math.max(0, prev - 1));
            setStep("result");
        } catch (err: unknown) {
            setSubmitError(err instanceof Error ? err.message : "An unexpected error occurred");
            setStep("quiz"); // Go back to quiz view to show error
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleContinue = async () => {
        nav?.setPrefetchedCourseData(courseId, null);
        await invalidateCourseCore(queryClient, courseId);
        navigate(`/courses/${courseId}`);
    };

    if (loading) {
        // Skeleton UI while the placement quiz loads
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl overflow-hidden animate-pulse">
                    {/* Mock header */}
                    <div className="bg-indigo-600 p-8 text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
                        <div className="relative z-10 space-y-3">
                            <div className="h-7 w-48 bg-white/30 rounded-md" />
                            <div className="h-4 w-64 bg-white/20 rounded-md" />
                        </div>
                    </div>

                    {/* Mock body */}
                    <div className="p-8 space-y-8">
                        {/* Stats cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[1, 2, 3].map((key) => (
                                <div
                                    key={key}
                                    className="flex flex-col items-center p-4 bg-indigo-50 rounded-xl space-y-2"
                                >
                                    <div className="w-8 h-8 bg-indigo-200 rounded-full" />
                                    <div className="h-4 w-20 bg-indigo-200 rounded-md" />
                                    <div className="h-3 w-16 bg-indigo-100 rounded-md" />
                                </div>
                            ))}
                        </div>

                        {/* Bullet points */}
                        <div className="space-y-3">
                            {[1, 2].map((key) => (
                                <div key={key} className="flex items-start gap-3">
                                    <div className="w-5 h-5 rounded-full bg-green-100 mt-0.5 flex-shrink-0" />
                                    <div className="h-4 w-full bg-gray-100 rounded-md" />
                                </div>
                            ))}
                        </div>

                        {/* CTA button placeholder */}
                        <div className="w-full h-11 bg-indigo-100 rounded-xl" />
                    </div>
                </div>
            </div>
        );
    }

    if (isMaxAttemptsReached || isAlreadyTaken) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-10 text-center">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
                        <CheckCircle className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2 font-outfit">Assessment Completed</h2>
                    <p className="text-gray-600 mb-4 font-inter text-sm">
                        You have already completed the placement assessment for this course.
                    </p>
                    {placementLevel && (
                        <div className="mb-6 p-3.5 bg-indigo-50 border border-indigo-100 rounded-xl text-xs font-semibold text-indigo-900">
                            Your Placement Level: <span className="font-bold text-indigo-700">{placementLevel}</span>
                        </div>
                    )}
                    <button
                        onClick={handleContinue}
                        className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-base shadow-lg transition-colors cursor-pointer"
                    >
                        Continue to Course
                    </button>
                </div>
            </div>
        );
    }

    if (error || !quiz) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center space-y-4">
                    <AlertCircle className="w-12 h-12 text-amber-500 mx-auto" />
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 mb-1">
                            {error || "Placement Assessment Unavailable"}
                        </h2>
                        <p className="text-sm text-gray-600">
                            {error ?? "We couldn't load the placement assessment for this course right now."}
                        </p>
                    </div>
                    <div className="pt-2 flex flex-col sm:flex-row gap-3 justify-center">
                        <Link
                            to={`/courses/${courseId}`}
                            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl text-sm shadow-sm transition-colors text-center"
                        >
                            Return to Course
                        </Link>
                        <Link
                            to="/courses"
                            className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold rounded-xl text-sm transition-colors text-center"
                        >
                            All Courses
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Intro View
    if (step === "intro") {
        return (
            <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-slate-50 flex items-center justify-center p-4">
                <div className="max-w-3xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden border border-indigo-100/70" dir={currentLanguage === "Arabic" ? "rtl" : "ltr"}>
                    <div className="relative bg-gradient-to-r from-indigo-600 via-indigo-500 to-sky-500 px-8 pt-8 pb-7 text-white">
                        <div className="absolute inset-0 opacity-20">
                            <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/20 rounded-full blur-3xl" />
                            <div className="absolute right-10 bottom-0 w-32 h-32 bg-indigo-300/30 rounded-full blur-3xl" />
                        </div>
                        <div className="relative z-10 max-w-xl mx-auto text-center space-y-3">
                            <div className="flex flex-wrap items-center justify-center gap-2">
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold tracking-wide uppercase">
                                    <Clock className="w-3.5 h-3.5" />
                                    ~{estimatedMinutes} min
                                </span>
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold tracking-wide uppercase">
                                    {attemptsRemaining > 0 ? "1 attempt available" : "0 attempts remaining"}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => handleSwitchLanguage(currentLanguage === "English" ? "Arabic" : "English")}
                                    disabled={switchingLanguage}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-white/25 hover:bg-white/35 backdrop-blur-sm px-3 py-1 text-xs font-bold text-white transition active:scale-95 cursor-pointer border border-white/30 shadow-xs"
                                    title={`Currently in ${currentLanguage}. Click to switch to ${currentLanguage === "English" ? "Arabic" : "English"}`}
                                >
                                    {switchingLanguage ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                        <Languages className="w-3.5 h-3.5" />
                                    )}
                                    <span>{currentLanguage === "English" ? "العربية" : "English"}</span>
                                </button>
                            </div>
                            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{quiz.title}</h1>
                            <p className="text-sm md:text-base text-indigo-100">
                                Find your ideal starting point so you skip what you already know and dive into what matters most.
                            </p>
                            <div className="flex flex-wrap justify-center gap-2 text-[10px] md:text-xs text-indigo-100/90">
                                <span className="px-2 py-0.5 rounded-full border border-white/20 bg-white/5">
                                    Adaptive
                                </span>
                                <span className="px-2 py-0.5 rounded-full border border-white/20 bg-white/5">
                                    Multi‑level
                                </span>
                                <span className="px-2 py-0.5 rounded-full border border-white/20 bg-white/5">
                                    Single attempt
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="p-8 space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <div className="flex flex-col items-center p-4 bg-indigo-50/70 rounded-2xl text-center border border-indigo-100">
                                <BookOpen className="w-8 h-8 text-indigo-600 mb-2" />
                                <span className="text-xs font-medium uppercase tracking-wide text-indigo-700">
                                    Levels
                                </span>
                                <span className="mt-1 text-lg font-semibold text-gray-900">
                                    {levels.length} {levels.length === 1 ? "Level" : "Levels"}
                                </span>
                                <span className="mt-1 text-xs text-gray-500">
                                    From foundations to advanced topics
                                </span>
                            </div>
                            <div className="flex flex-col items-center p-4 bg-sky-50/70 rounded-2xl text-center border border-sky-100">
                                <Brain className="w-8 h-8 text-sky-600 mb-2" />
                                <span className="text-xs font-medium uppercase tracking-wide text-sky-700">
                                    Questions
                                </span>
                                <span className="mt-1 text-lg font-semibold text-gray-900">
                                    {totalQuestions} {totalQuestions === 1 ? "Question" : "Questions"}
                                </span>
                                <span className="mt-1 text-xs text-gray-500">
                                    About {averageQuestionsPerLevel} per level, increasing in difficulty
                                </span>
                            </div>
                            <div className="flex flex-col items-center p-4 bg-emerald-50/70 rounded-2xl text-center border border-emerald-100">
                                <Zap className="w-8 h-8 text-emerald-600 mb-2" />
                                <span className="text-xs font-medium uppercase tracking-wide text-emerald-700">
                                    Placement
                                </span>
                                <span className="mt-1 text-lg font-semibold text-gray-900">Smart starting point</span>
                                <span className="mt-1 text-xs text-gray-500">
                                    Your score chooses where you enter the course
                                </span>
                            </div>
                        </div>

                        {levels.length > 0 && (
                            <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-5 space-y-4">
                                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                    <Trophy className="w-4 h-4 text-indigo-600" />
                                    Levels in this placement quiz
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {levels.map((l, idx) => {
                                        const isPassed = passedLevels.some(pl => pl.toLowerCase() === l.name.toLowerCase());
                                        const isFailed = failedLevels.some(fl => fl.toLowerCase() === l.name.toLowerCase()) && !isPassed;
                                        const isCurrentStart = idx === minAllowedLevelIdx && !isPassed;
                                        return (
                                            <span
                                                key={l.name}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
                                                    isPassed
                                                        ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                                        : isFailed
                                                        ? "bg-rose-50 text-rose-800 border-rose-200"
                                                        : isCurrentStart
                                                        ? "bg-indigo-50 text-indigo-800 border-indigo-300 font-semibold"
                                                        : "bg-white text-gray-700 border-gray-200"
                                                }`}
                                            >
                                                {isPassed ? (
                                                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                                                ) : isFailed ? (
                                                    <XCircle className="w-3.5 h-3.5 text-rose-500" />
                                                ) : (
                                                    <span className={`w-4 h-4 rounded-full text-[10px] flex items-center justify-center font-bold ${
                                                        isCurrentStart ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-700"
                                                    }`}>
                                                        {idx + 1}
                                                    </span>
                                                )}
                                                {l.name}
                                                {isPassed && <span className="text-[10px] uppercase font-bold text-emerald-700">(Passed)</span>}
                                                {isFailed && <span className="text-[10px] uppercase font-bold text-rose-600">(Not Passed)</span>}
                                                {isCurrentStart && minAllowedLevelIdx > 0 && !isFailed && <span className="text-[10px] uppercase font-bold text-indigo-700">(Current)</span>}
                                            </span>
                                        );
                                    })}
                                </div>
                                {passedLevels.length > 0 && minAllowedLevelIdx < levels.length && (
                                    <div className="bg-indigo-50/80 border border-indigo-100 rounded-xl p-3 text-xs text-indigo-900 flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                                        <p>
                                            You have already passed <span className="font-semibold">{passedLevels.join(", ")}</span>! This assessment will start directly on the <span className="font-semibold">{levels[minAllowedLevelIdx]?.name}</span> level.
                                        </p>
                                    </div>
                                )}
                                <p className="text-xs text-gray-500">
                                    {minAllowedLevelIdx > 0
                                        ? `You will begin directly with the ${levels[minAllowedLevelIdx]?.name} questions.`
                                        : "You'll move through these levels in order. How far you get helps us pick the best starting point for you."}
                                </p>
                                <ol className="space-y-1.5 text-sm text-gray-600 list-decimal list-inside">
                                    <li>
                                        Answer questions <span className="font-semibold">one level at a time</span>. You only move
                                        forward when you&apos;ve shown enough mastery.
                                    </li>
                                    <li>
                                        Once you stop progressing, we use your highest passed level to <span className="font-semibold">choose your
                                        starting lessons</span>.
                                    </li>
                                    <li>
                                        You can&apos;t &quot;fail&quot; this quiz&mdash;it simply calibrates the course to your current ability.
                                    </li>
                                </ol>
                            </div>
                        )}

                        <div className="space-y-3">
                            <button
                                onClick={handleStart}
                                className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-2xl text-lg shadow-md hover:shadow-xl transition-all flex items-center justify-center gap-2 group"
                            >
                                {minAllowedLevelIdx > 0 ? `Start ${levels[minAllowedLevelIdx]?.name} Assessment` : "Start placement assessment"}
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-2">
                                <Link
                                    to={`/courses/${courseId}`}
                                    className="text-xs font-semibold text-gray-500 hover:text-indigo-600 transition-colors"
                                >
                                    ← Skip for now &amp; return to course
                                </Link>
                                <p className="text-xs text-gray-400">
                                    Single attempt assessment
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Quiz View
    if (step === "quiz" && currentQuestion) {
        const levelProgress = ((questionIdxInLevel + 1) / currentLevel.questions.length) * 100;
        const isAnswerSelected = selectedAnswers[currentQuestion.id] !== undefined;

        const getOptionLabel = (option: string | { label?: string; text?: string; value?: string } | null | undefined) => {
            if (typeof option === 'string') return option;
            if (typeof option === 'object' && option !== null) {
                return option.label || option.text || option.value || JSON.stringify(option);
            }
            return String(option);
        };

        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col min-h-[600px]" dir={currentLanguage === "Arabic" ? "rtl" : "ltr"}>
                    <div className="bg-white border-b border-gray-100 p-4 sm:p-6 flex justify-between items-center gap-3">
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-gray-500">Question {questionIdxInLevel + 1} of {currentLevel.questions.length}</span>
                            <button
                                type="button"
                                onClick={() => handleSwitchLanguage(currentLanguage === "English" ? "Arabic" : "English")}
                                disabled={switchingLanguage}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 transition-colors border border-slate-200 active:scale-95 cursor-pointer shadow-xs"
                                title={`Currently in ${currentLanguage}. Click to switch to ${currentLanguage === "English" ? "Arabic" : "English"}`}
                            >
                                {switchingLanguage ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-600" />
                                ) : (
                                    <Languages className="w-3.5 h-3.5 text-indigo-600" />
                                )}
                                <span>{currentLanguage === "English" ? "العربية" : "English"}</span>
                            </button>
                        </div>
                        <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-indigo-600 transition-all duration-500 ease-out"
                                style={{ width: `${levelProgress}%` }}
                            ></div>
                        </div>
                    </div>

                    <div className="p-8 flex-grow flex flex-col">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                            {levels.map((l, idx) => {
                                const isPassed = passedLevels.some(pl => pl.toLowerCase() === l.name.toLowerCase());
                                const isFailed = failedLevels.some(fl => fl.toLowerCase() === l.name.toLowerCase()) && !isPassed;
                                const isCurrent = idx === currentLevelIdx;
                                return (
                                    <span
                                        key={l.name}
                                        className={`px-2.5 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-wider flex items-center gap-1 ${
                                            isCurrent
                                                ? "bg-indigo-600 text-white shadow-sm"
                                                : isPassed
                                                ? "bg-emerald-100 text-emerald-800"
                                                : isFailed
                                                ? "bg-rose-100 text-rose-800"
                                                : "bg-gray-100 text-gray-400"
                                        }`}
                                    >
                                        {isPassed && <CheckCircle className="w-3 h-3 text-emerald-600" />}
                                        {isFailed && !isCurrent && <XCircle className="w-3 h-3 text-rose-500" />}
                                        {l.name}
                                        {isPassed && " (Passed)"}
                                        {isFailed && !isCurrent && " (Not Passed)"}
                                    </span>
                                );
                            })}
                        </div>
                        {/* Sized down from text-2xl so a long question fits the card at
                            its min height instead of growing it. Shrinking the type keeps
                            every question the same size; letting the card grow to the
                            question would make them all differ, which is what the report
                            explicitly asked to avoid. */}
                        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-6 leading-relaxed">
                            {currentQuestion.question_text}
                        </h2>

                        <div className="space-y-4">
                            {currentQuestion.options.map((option, idx) => {
                                const label = getOptionLabel(option);
                                return (
                                    <button
                                        key={idx}
                                        onClick={() => handleAnswerSelect(currentQuestion.id, label)}
                                        className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center justify-between group ${selectedAnswers[currentQuestion.id] === label
                                            ? "border-indigo-600 bg-indigo-50"
                                            : "border-gray-200 hover:border-indigo-200 hover:bg-gray-50"
                                            }`}
                                    >
                                        <span className={`font-medium ${selectedAnswers[currentQuestion.id] === label ? "text-indigo-900" : "text-gray-700"}`}>
                                            {label}
                                        </span>
                                        {selectedAnswers[currentQuestion.id] === label && (
                                            <CheckCircle className="w-5 h-5 text-indigo-600" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {submitError && (
                            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-xl flex items-center gap-2">
                                <AlertCircle className="w-5 h-5" />
                                <p className="text-sm font-medium">{submitError}</p>
                            </div>
                        )}
                    </div>

                    <div className="p-6 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                        <button
                            type="button"
                            onClick={handlePrevious}
                            disabled={!canGoPrevious || isEvaluating || isSubmitting}
                            className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                                canGoPrevious && !isEvaluating && !isSubmitting
                                    ? "bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900 shadow-sm"
                                    : "opacity-0 pointer-events-none"
                            }`}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Back
                        </button>

                        <button
                            type="button"
                            onClick={handleNext}
                            disabled={!isAnswerSelected || isEvaluating || isSubmitting}
                            className={`px-8 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                                isAnswerSelected && !isEvaluating && !isSubmitting
                                    ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-md"
                                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                            }`}
                        >
                            {isEvaluating || isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {isSubmitting ? "Submitting..." : "Evaluating..."}
                                </>
                            ) : (
                                <>
                                    {isLastLevel && questionIdxInLevel === currentLevel.questions.length - 1 ? "Finish" : "Next Question"}
                                    <ChevronRight className="w-4 h-4" />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Level Interstitial View (Only for non-terminal passed levels)
    if (step === "level_interstitial" && levelResult) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-12 text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Trophy className="w-10 h-10 text-green-600" />
                    </div>

                    <h2 className="text-3xl font-bold text-gray-900 mb-2 font-outfit">Level Passed!</h2>
                    <p className="text-lg text-gray-600 mb-8 font-inter">
                        You scored <span className="font-bold text-indigo-600">{levelResult.percentage}%</span> on the <span className="font-bold">{currentLevel?.name}</span> assessment.
                    </p>

                    <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left">
                        <h3 className="font-semibold text-gray-900 mb-2 font-outfit">Excellent work!</h3>
                        <p className="text-gray-600 font-inter">
                            You&apos;ve demonstrated mastery of this level and are ready for the next challenge.
                        </p>
                    </div>

                    <button
                        onClick={handleProceed}
                        className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                    >
                        Proceed to Next Level <ArrowRight className="w-6 h-6" />
                    </button>
                </div>
            </div>
        );
    }

    // Merged Result View
    if (step === "result") {
        const isSponsored = accessMode === "SPONSORED" || isOrgMember;
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl overflow-hidden p-8 sm:p-12">
                    <div className="text-center mb-8">
                        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 ${levelResult?.passed ? 'bg-green-100' : 'bg-orange-100'}`}>
                            {levelResult?.passed ? (
                                <Trophy className={`w-10 h-10 ${levelResult.passed ? 'text-green-600' : 'text-orange-600'}`} />
                            ) : (
                                <AlertCircle className="w-10 h-10 text-orange-600" />
                            )}
                        </div>

                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 font-outfit">
                            {levelResult?.passed ? "Assessment Complete!" : "Level Assessment Complete"}
                        </h2>
                        <p className="text-base sm:text-lg text-gray-600 font-inter">
                            You scored <span className="font-bold text-indigo-600">{levelResult?.percentage}%</span> on the <span className="font-bold">{currentLevel?.name}</span> assessment.
                        </p>
                    </div>

                    <div className="space-y-6 mb-8">
                        <div className="bg-gray-50 rounded-2xl p-5 text-center border border-gray-100">
                            <p className="text-gray-500 uppercase tracking-widest text-xs font-bold mb-1.5">Final Result</p>
                            <p className="text-gray-900 text-xl font-outfit">
                                You have been placed at the <span className="font-bold text-indigo-600">{placementLevel}</span> level.
                            </p>
                        </div>

                        {isSponsored ? (
                            <div className="bg-white rounded-2xl p-6 border border-indigo-200">
                                <h3 className="font-bold text-gray-900 mb-3 font-outfit flex items-center gap-2">
                                    <BookOpen className="w-5 h-5 text-indigo-600" />
                                    {organizationName ? `Included via ${organizationName}` : "Included via your organization"}
                                </h3>
                                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-5">
                                    <p className="text-sm text-indigo-900 font-inter">
                                        {organizationName
                                            ? `This course is sponsored by ${organizationName}, so you can continue without checkout.`
                                            : "This course is sponsored for your organization, so you can continue without checkout."}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white rounded-2xl p-6 border border-gray-200 space-y-4">
                                <div>
                                    <h3 className="font-bold text-gray-900 font-outfit flex items-center gap-2 text-base sm:text-lg">
                                        <BookOpen className="w-5 h-5 text-indigo-600" />
                                        Choose your access plan
                                    </h3>
                                    <p className="text-xs text-gray-500 mt-1 font-inter">
                                        Select an access option below before proceeding to checkout:
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* Option A: Lifetime Purchase */}
                                    <button
                                        type="button"
                                        onClick={() => setSelectedPlanType("lifetime")}
                                        className={`rounded-2xl p-5 text-left transition-all relative flex flex-col justify-between ${
                                            selectedPlanType === "lifetime"
                                                ? "border-2 border-indigo-600 bg-indigo-50/50 shadow-md ring-2 ring-indigo-500/20"
                                                : "border border-gray-200 bg-white hover:border-indigo-200 hover:bg-slate-50/60"
                                        }`}
                                    >
                                        <div className="space-y-2">
                                            <div className="flex items-start justify-between gap-2">
                                                <span className="text-[10px] font-bold tracking-widest text-indigo-700 uppercase bg-indigo-100/70 px-2 py-0.5 rounded-md">
                                                    Option A · Lifetime
                                                </span>
                                                <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                                                    selectedPlanType === "lifetime" ? "bg-indigo-600 text-white" : "border border-gray-300 bg-white"
                                                }`}>
                                                    {selectedPlanType === "lifetime" && <CheckCircle className="w-3.5 h-3.5" />}
                                                </div>
                                            </div>

                                            <h4 className="text-base font-bold text-gray-900 font-outfit">
                                                Lifetime Purchase
                                            </h4>
                                            <p className="text-xs text-indigo-600 font-semibold">
                                                {placementLevel || "Placed"} Level Access
                                            </p>
                                        </div>

                                        <ul className="mt-4 text-xs text-gray-700 space-y-2 font-inter border-t border-gray-100 pt-3">
                                            <li className="flex items-start gap-1.5">
                                                <span className="text-indigo-600 font-bold">•</span>
                                                <span><strong className="font-semibold">One-time payment</strong> for the {placementLevel || "placed"} level.</span>
                                            </li>
                                            <li className="flex items-start gap-1.5">
                                                <span className="text-indigo-600 font-bold">•</span>
                                                <span>Keep lifetime access <strong className="font-semibold">without renewals</strong>.</span>
                                            </li>
                                            <li className="flex items-start gap-1.5">
                                                <span className="text-indigo-600 font-bold">•</span>
                                                <span>Full exercises &amp; completion certificate.</span>
                                            </li>
                                        </ul>
                                    </button>

                                    {/* Option B: Yearly Subscription */}
                                    <button
                                        type="button"
                                        onClick={() => setSelectedPlanType("yearly")}
                                        className={`rounded-2xl p-5 text-left transition-all relative flex flex-col justify-between ${
                                            selectedPlanType === "yearly"
                                                ? "border-2 border-emerald-600 bg-emerald-50/50 shadow-md ring-2 ring-emerald-500/20"
                                                : "border border-gray-200 bg-white hover:border-emerald-200 hover:bg-slate-50/60"
                                        }`}
                                    >
                                        <div className="space-y-2">
                                            <div className="flex items-start justify-between gap-2">
                                                <span className="text-[10px] font-bold tracking-widest text-emerald-700 uppercase bg-emerald-100/70 px-2 py-0.5 rounded-md">
                                                    Option B · Best Value
                                                </span>
                                                <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                                                    selectedPlanType === "yearly" ? "bg-emerald-600 text-white" : "border border-gray-300 bg-white"
                                                }`}>
                                                    {selectedPlanType === "yearly" && <CheckCircle className="w-3.5 h-3.5" />}
                                                </div>
                                            </div>

                                            <h4 className="text-base font-bold text-gray-900 font-outfit">
                                                Yearly Subscription
                                            </h4>
                                            <p className="text-xs text-emerald-600 font-semibold">
                                                All Courses &amp; Levels Included
                                            </p>
                                        </div>

                                        <ul className="mt-4 text-xs text-gray-700 space-y-2 font-inter border-t border-gray-100 pt-3">
                                            <li className="flex items-start gap-1.5">
                                                <span className="text-emerald-600 font-bold">•</span>
                                                <span><strong className="font-semibold">Unlimited access</strong> to all courses and all levels.</span>
                                            </li>
                                            <li className="flex items-start gap-1.5">
                                                <span className="text-emerald-600 font-bold">•</span>
                                                <span>Billed annually — <strong className="font-semibold">cancel anytime</strong>.</span>
                                            </li>
                                            <li className="flex items-start gap-1.5">
                                                <span className="text-emerald-600 font-bold">•</span>
                                                <span>Unlimited verified certificates included.</span>
                                            </li>
                                        </ul>
                                    </button>
                                </div>

                                <p className="text-xs text-gray-500 font-inter text-center pt-1">
                                    {selectedPlanType === "lifetime"
                                        ? `Selected: Lifetime access for the ${placementLevel || "placed"} level.`
                                        : "Selected: Yearly Subscription for all courses and levels."}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="space-y-3">
                        {isSponsored ? (
                            <Link
                                to={`/courses/${courseId}`}
                                className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 group"
                            >
                                Continue to course
                                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        ) : selectedPlanType === "lifetime" ? (
                            <Link
                                to={
                                    suggestedPlanId != null
                                        ? `/checkout/${suggestedPlanId}`
                                        : `/courses/${courseId}/plans`
                                }
                                className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 group"
                            >
                                <span>Proceed with Lifetime Access ({placementLevel || "Placed"} Level)</span>
                                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        ) : (
                            <Link
                                to={
                                    yearlyPlanId != null
                                        ? `/checkout/${yearlyPlanId}`
                                        : `/pricing`
                                }
                                className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 group"
                            >
                                <span>Proceed with Yearly Subscription</span>
                                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        )}

                        {!isSponsored && (
                            <Link
                                to={`/courses/${courseId}/plans`}
                                className="w-full py-2.5 bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-lg text-sm border border-gray-200 shadow-sm hover:shadow transition-all flex items-center justify-center gap-2"
                            >
                                View all levels &amp; pricing on course page
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return null;
}
